import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const AdminAuthContext = createContext(null);

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const backendUrl = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${backendUrl}/api`;

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('admin_token'));
  const navigate = useNavigate();
  
  const checkAuth = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API}/admin/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const adminData = await response.json();
        setAdmin(adminData);
      } else {
        setAdmin(null);
        localStorage.removeItem('admin_token');
        setToken(null);
      }
    } catch (error) {
      console.error('Admin auth check failed:', error);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, [token]);
  
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);
  
  const login = async (email, password) => {
    // Error messages mapping
    const errorMessages = {
      'EMAIL_NOT_FOUND': 'No account found with this email address',
      'INVALID_PASSWORD': 'Incorrect password. Please try again',
      'ACCOUNT_DISABLED': 'Your account has been disabled. Please contact an administrator',
      'LOGIN_FAILED': 'Login failed. Please try again'
    };

    try {
      const response = await axios.post(`${API}/admin/auth/login`, { email, password });
      const data = response.data;
      
      localStorage.setItem('admin_token', data.token);
      setToken(data.token);
      setAdmin(data.admin);
      return data.admin;
    } catch (error) {
      // Handle axios error response
      if (error.response && error.response.data) {
        const errorCode = error.response.data.detail || 'LOGIN_FAILED';
        throw new Error(errorMessages[errorCode] || errorMessages['LOGIN_FAILED']);
      }
      // Network or other errors
      if (error.code === 'ERR_NETWORK' || error.message.includes('Network')) {
        throw new Error('Unable to connect to server. Please check your connection.');
      }
      throw new Error('Login failed. Please try again.');
    }
  };
  
  const logout = async () => {
    try {
      await fetch(`${API}/admin/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    navigate('/admin/login');
  };
  
  const hasRole = (allowedRoles) => {
    if (!admin) return false;
    return allowedRoles.includes(admin.role);
  };

  const homePathForRole = (role) => (
    role === 'umpire' ? '/admin/events' : '/admin/dashboard'
  );
  
  const value = {
    admin,
    loading,
    token,
    login,
    logout,
    hasRole,
    homePathForRole,
    isAuthenticated: !!admin
  };
  
  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Protected Route Component for Admin
export const AdminProtectedRoute = ({ children, allowedRoles = null }) => {
  const { admin, loading, isAuthenticated, hasRole, homePathForRole } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/admin/login', { state: { from: location }, replace: true });
      return;
    }

    if (!loading && isAuthenticated && admin?.role === 'umpire' && location.pathname === '/admin/dashboard') {
      navigate('/admin/events', { replace: true });
      return;
    }
    
    if (!loading && isAuthenticated && allowedRoles && !hasRole(allowedRoles)) {
      navigate(homePathForRole(admin?.role), { replace: true });
    }
  }, [loading, isAuthenticated, navigate, location, allowedRoles, hasRole, admin, homePathForRole]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) return null;
  if (admin?.role === 'umpire' && location.pathname === '/admin/dashboard') return null;
  if (allowedRoles && !hasRole(allowedRoles)) return null;
  
  return children;
};

export default AdminAuthContext;
